<template>
    <div class="app-header-wrapper">
        <v-app-bar
            flat
            height="72"
            class="app-header"
            :class="{ 'app-header--page-cta': headerCta }"
        >
            <div class="app-header__section app-header__section--left">
                <RouterLink
                    :to="isAuthenticated ? '/dashboard' : '/'"
                    class="app-header__logo"
                    aria-label="Перейти на главную"
                >
                    <SvgLogo :width="logoWidth" />
                </RouterLink>
            </div>

            <div v-if="hasCenterContent" class="app-header__section app-header__section--center">
                <!--
          Issue #8 — CTA "Стать наставником" виден только когда заявки
          нет вообще (phase=`none`). Как только пользователь начал
          процесс (draft/pending/rejected/changes_requested) — CTA
          уступает место `<UserRoleChips>`, который полноценно ведёт
          в правильную точку флоу.
        -->
                <!--
          Заметное действие текущей страницы (shared/lib/headerCta) — по центру
          шапки: на claim-странице это «Забрать аккаунт», то, ради чего страницу
          и открыли. Центр перебивает «Стать наставником»: два предложения
          одновременно спорили бы друг с другом.
        -->
                <button
                    v-if="headerCta"
                    type="button"
                    class="app-header__page-cta"
                    :class="`app-header__page-cta--${headerCta.tone ?? 'violet'}`"
                    :data-testid="headerCta.testId"
                    @click="headerCta.onClick()"
                >
                    <span class="app-header__page-cta-label">{{ headerCta.label }}</span>
                </button>

                <button
                    v-else-if="showBecomeMentorCta"
                    class="app-header__cta"
                    @click="handleNavigate('/become-mentor')"
                >
                    <span class="app-header__cta-icon" aria-hidden="true">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 26 26"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4.44444 2V6.88889M2 4.44444H6.88889M5.66667 19.1111V24M3.22222 21.5556H8.11111M14.2222 2L17.0159 10.381L24 13L17.0159 15.619L14.2222 24L11.4286 15.619L4.44444 13L11.4286 10.381L14.2222 2Z"
                                stroke="currentColor"
                                stroke-width="2.04545"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    Стать наставником
                </button>
            </div>

            <!--
          23.09: гостевой вход из мобильной шапки убран (решение владельца —
          он не так уж там нужен и остаётся в футере). Секция целиком не
          рендерится, когда в ней нечего показывать.
        -->
            <div v-if="hasRightCluster" class="app-header__section app-header__section--right">
                <!--
          Issue #8 — заменили статический role-badge на chip-пару,
          которая визуализирует и текущую роль, и процесс перехода
          ученик → наставник (черновик / на проверке / отказ / одобрена).
          Tooltip с описанием — на focus и hover (Vuetify v-tooltip).

          2026-06-01: переставлен ПЕРЕД колокольчиком. Статус-пилюля сейчас
          несёт и payout-missing nudge (см. payoutMissing-проп), поэтому
          должна быть первой в кластере — это самый «громкий» элемент.
        -->
                <UserRoleChips
                    v-if="isAuthenticated && userRole"
                    class="app-header__role-chips"
                    :class="{ 'app-header__role-chips--mobile': isMobile }"
                    :current-role="userRole === 'mentor' ? 'mentor' : 'student'"
                    :application-phase="mentorAppPhase"
                    :rejection-reason="mentorAppRejectionReason"
                    :payout-missing="payoutMissing"
                    :compact="chipCompact"
                    @click-mentor-chip="onMentorChipClick"
                    @click-payout-attach="onPayoutAttachClick"
                />

                <NotificationComponent
                    class="app-header__bell"
                    :avatar-url="avatarUrl"
                    :notification-count="notifications.unreadCount"
                    :is-authenticated="isAuthenticated"
                    @login-click="handleNavigate('/login')"
                />

                <v-menu
                    v-if="isAuthenticated && !isMobile"
                    v-model="profileMenuOpen"
                    :close-on-content-click="false"
                    transition="fade-transition"
                    :transition-duration="300"
                >
                    <template #activator="{ props }">
                        <button
                            class="app-header__profile"
                            v-bind="props"
                            aria-label="Открыть меню профиля"
                        >
                            <UserAvatar
                                :user="user"
                                :size="48"
                                alt="Профиль"
                                class="app-header__profile-image"
                            />
                        </button>
                    </template>
                    <ProfileMenu @close="profileMenuOpen = false" />
                </v-menu>

                <button
                    v-if="isMobile && isAuthenticated"
                    class="app-header__profile app-header__profile--mobile"
                    aria-label="Открыть меню"
                    @click="toggleDrawer"
                >
                    <UserAvatar
                        :user="user"
                        :size="48"
                        alt="Профиль"
                        class="app-header__profile-image"
                    />
                </button>
            </div>
        </v-app-bar>

        <HeaderMobileDrawer
            v-if="isMobile && isAuthenticated"
            v-model:open="mobileDrawer"
            :is-authenticated="isAuthenticated"
            :user-name="userName"
            :user-email="userEmail"
            scrim="rgba(25, 21, 45, 0.35)"
            @navigate="handleNavigate"
            @logout="handleLogout"
        />

        <LogoutConfirmDialog
            v-model="showLogoutConfirm"
            :loading="logoutLoading"
            @confirm="confirmLogout"
        />
    </div>
</template>

<script setup lang="ts">
/* eslint-disable */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';
import NotificationComponent from './NotificationComponent.vue';
import HeaderMobileDrawer from './HeaderMobileDrawer.vue';
import SvgLogo from '../common/SvgLogo.vue';
import { useNotifications } from '../../../store/notifications';
import { getSafeAvatarUrl } from '@/shared/lib/avatar';
import LogoutConfirmDialog from '../common/LogoutConfirmDialog.vue';
import ProfileMenu from './ProfileMenu.vue';
import UserAvatar from '../common/UserAvatar.vue';
import UserRoleChips from '../common/UserRoleChips.vue';
import { useMentorApplicationStatus, type Phase } from '@/composables/useMentorApplicationStatus';
import { useSellingReadiness } from '@/composables/useSellingReadiness';
import { headerCta } from '@/shared/lib/headerCta';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { mdAndDown, smAndDown } = useDisplay();
const notifications = useNotifications();

const mobileDrawer = ref(false);
const showLogoutConfirm = ref(false);
const logoutLoading = ref(false);
const profileMenuOpen = ref(false);

const isMobile = computed(() => mdAndDown.value);

// Мобильный знак меньше десктопного — шапка там уже. Под кнопку страницы знак
// больше не ужимается: секции наконец делят строку через flex, а не внахлёст
// (см. media-запрос ≤640px), и подбирать ширину скриптом незачем.
const logoWidth = computed<number>(() => (isMobile.value ? 112 : 140));
// Компактный режим чипа роли — только на узком вьюпорте (<960px), где суффикс
// чипа скрыт по ширине (см. `.app-header__role-chips--mobile` media-query).
// Именно здесь payout-missing нужно нести янтарной точкой + tap→tooltip.
const chipCompact = computed(() => smAndDown.value);
const isAuthenticated = computed<boolean>(() => store.getters.isAuthenticated);

const user = computed(() => store.state.user);
const guestLabel = computed(() => {
    const id = user.value?.id;
    return `Гость${id ? ` #${id}` : ''}`;
});
const userName = computed(() => {
    if (!isAuthenticated.value) return guestLabel.value;
    const first = user.value?.first_name?.trim();
    const last = user.value?.last_name?.trim();
    const username = user.value?.username?.trim();
    return [first, last].filter(Boolean).join(' ') || username || guestLabel.value;
});
const userEmail = computed(() => (isAuthenticated.value ? (user.value?.email ?? '') : ''));
const avatarUrl = computed(() => getSafeAvatarUrl(user.value?.avatar_url));
const userRole = computed(() => store.getters.userRole as string | null);
const canBecomeMentor = computed(() => userRole.value !== 'mentor');

// Гостю на мобиле в правом кластере не остаётся ничего: вход из шапки убран
// (он есть в футере), а колокол и профиль — только авторизованным. Пустую
// flex-секцию не рендерим вовсе: она забирала бы себе зазор между секциями.
const hasRightCluster = computed<boolean>(() => isAuthenticated.value || !isMobile.value);

// Issue #8 — статус наставник-заявки для chip'а в header.
// Composable сам подтягивает GET /mentor-applications/me/ на mount.
const mentorAppStatus = useMentorApplicationStatus();
const mentorAppPhase = computed<Phase>(() => mentorAppStatus.phase.value);
const mentorAppRejectionReason = computed<string>(() => mentorAppStatus.rejectionReason.value);

// CTA «Стать наставником» — только пока заявки нет вовсе (issue #8) и только
// на десктопе: на мобиле центр шапки отдан действию страницы.
const showBecomeMentorCta = computed<boolean>(
    () =>
        isAuthenticated.value &&
        !isMobile.value &&
        canBecomeMentor.value &&
        mentorAppPhase.value === 'none',
);

// Пустой центр всё равно остаётся flex-элементом и съедает зазоры вокруг себя,
// а на узкой шапке лишние 24px — заметная потеря.
const hasCenterContent = computed<boolean>(
    () => headerCta.value !== null || showBecomeMentorCta.value,
);

// 2026-06-01: payout-missing визуализируется прямо внутри пилюли «Наставник»
// вместо отдельной sticky-полосы (которая занимала целую строку и раздражала).
// Источник истины — selling_readiness; approved_no_payout ⇒ карты нет.
const { value: sellingReadiness } = useSellingReadiness();
const payoutMissing = computed<boolean>(() => sellingReadiness.value === 'approved_no_payout');

function onMentorChipClick(phase: Phase): void {
    // none/draft/rejected/changes_requested → /become-mentor
    // pending → no-op (заявка уже на проверке, идти некуда)
    // approved → профиль наставника
    if (phase === 'pending') return;
    if (phase === 'approved') {
        handleNavigate('/dashboard');
        return;
    }
    handleNavigate('/become-mentor');
}

function onPayoutAttachClick(): void {
    handleNavigate('/ProfileSettings?tab=payment');
}

// NB: здесь жил `drawerNavItems` с бейджем/подсветкой календаря, который
// передавался в <HeaderMobileDrawer :nav-items>. Но HeaderMobileDrawer этот
// проп ИГНОРИРУЕТ (рендерит только <ProfileMenu/>), так что бейдж нигде не
// отрисовывался — это был мёртвый дубль живого индикатора в доке (`app-dock`
// в App.vue, который читает ту же `store.calendarSummary`). Удалено вместе с
// зависимостями (baseDrawerNav / mentorsCatalogEnabled / eventsCatalogEnabled /
// calendar*-computed). Живой календарь-индикатор — в App.vue.

const handleNavigate = (path: string) => {
    mobileDrawer.value = false;
    router.push(path);
};

const handleLogout = async () => {
    mobileDrawer.value = false;
    showLogoutConfirm.value = true;
};

const confirmLogout = async () => {
    logoutLoading.value = true;
    try {
        await store.dispatch('logout');
        router.push('/login');
    } catch (error) {
        // Даже если выход упал (сеть/бэкенд), чистим локальное состояние и уводим
        // на /login — иначе пользователь остаётся в приватном разделе. Тот же
        // паттерн, что в ProfileMenu.vue.
        console.error('Ошибка при выходе:', error);
        store.commit('clearUser');
        router.push('/login');
    } finally {
        logoutLoading.value = false;
        showLogoutConfirm.value = false;
    }
};

const toggleDrawer = () => {
    mobileDrawer.value = !mobileDrawer.value;
};

watch(
    () => route.path,
    () => {
        mobileDrawer.value = false;
    },
);

const loadNotificationsIfNeeded = async () => {
    if (isAuthenticated.value && !notifications.loaded) {
        await notifications.load();
    }
};

onMounted(loadNotificationsIfNeeded);

watch(isAuthenticated, () => {
    void loadNotificationsIfNeeded();
});
</script>

<style scoped>
.app-header-wrapper {
    position: fixed;
    top: var(--app-floating-gap-desktop);
    left: 0;
    right: 0;
    z-index: 1100;
    width: 100%;
    pointer-events: none;
}

.app-header-wrapper > * {
    pointer-events: auto;
}

.app-header {
    position: fixed !important;
    top: var(--app-floating-gap-desktop) !important;
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) !important;
    width: calc(100% - (var(--app-floating-gap-desktop) * 2)) !important;
    max-width: var(--app-header-max-width);
    margin: 0;
    padding: 0 12px;
    background: var(--app-surface-bg);
    backdrop-filter: blur(var(--app-surface-blur));
    border: 1px solid var(--app-surface-border);
    border-radius: var(--app-surface-radius);
    box-shadow: var(--app-surface-shadow);
}

.app-header__section {
    display: flex;
    align-items: center;
    gap: 16px;
}

.app-header__section--left {
    flex: 1 1 auto;
}

.app-header__section--center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
}

.app-header__section--right {
    display: flex;
    align-items: center;
    gap: 12px;
}

.app-header__logo {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
}

.app-header__burger {
    color: #2a2037;
    margin-left: 8px;
}

/* Действие страницы. Золотой тон — единственный такой акцент в приложении:
   он для действия, ради которого страницу и открыли. Контраст: #2F2410 на
   #F4C24B — 8.4:1. */
.app-header__page-cta {
    /* Центр шапки объявлен pointer-events: none (он абсолютный и растянут),
       и кнопка наследовала это — клик уходил в левую секцию под ней. */
    pointer-events: auto;
    min-height: 48px;
    padding: 0 22px;
    border: 0;
    border-radius: 999px;
    font: inherit;
    /* Кнопка называет главное действие страницы: 17px против 14px у соседей. */
    font-size: var(--t-body-lg);
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
}

/* Золото переливается: по ровной золотой заливке проходит светлый блик и
   уезжает за край. Прежний вариант двигал сам градиент, и в момент
   перезапуска анимации был виден шов — здесь блика в этот момент на кнопке
   уже нет, поэтому цикл незаметен.
   Текст #2F2410 на золоте (#F0B93F) — 7.9:1; под бликом фон светлеет, то есть
   контраст только растёт. */
.app-header__page-cta--gold {
    position: relative;
    overflow: hidden;
    background-image: linear-gradient(135deg, #f0b93f 0%, #f7d072 45%, #e2a82b 100%);
    color: #2f2410;
    box-shadow: 0 8px 22px rgba(226, 168, 43, 0.35);
}

.app-header__page-cta--gold::after {
    content: '';
    position: absolute;
    top: -50%;
    bottom: -50%;
    left: 0;
    width: 42%;
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.65) 50%,
        rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
    animation: app-header-gold-sheen 3.6s ease-in-out infinite;
}

/* Блик стартует и заканчивает ЗА пределами кнопки, а последнюю треть цикла
   стоит за правым краем — получается пауза между проходами. */
@keyframes app-header-gold-sheen {
    0% {
        transform: skewX(-18deg) translateX(-160%);
    }

    65%,
    100% {
        transform: skewX(-18deg) translateX(340%);
    }
}

.app-header__page-cta-label {
    position: relative;
    z-index: 1;
}

/* Движение — украшение, а не смысл: в reduce-режиме блик не появляется. */
@media (prefers-reduced-motion: reduce) {
    .app-header__page-cta--gold::after {
        display: none;
    }
}

.app-header__page-cta--violet {
    background: #7b65f7;
    color: #ffffff;
}

.app-header__page-cta:focus-visible {
    outline: 2px solid #2f285a;
    outline-offset: 3px;
}

@media (hover: hover) {
    .app-header__page-cta--gold:hover {
        filter: brightness(1.04);
    }

    .app-header__page-cta--violet:hover {
        background: #6a54e6;
    }
}

@media (max-width: 640px) {
    .app-header__page-cta {
        min-height: 42px;
        padding: 0 14px;
        font-size: var(--t-body);
    }

    /* Центр шапки абсолютен и центрирован по экрану — с кнопкой страницы он
       ложился поверх логотипа. На мобиле центр возвращается в поток, и части
       шапки делят строку. Ширину кнопка держит: сжатая подпись теряет смысл, а
       `text-overflow` при нехватке даже в полпикселя съедает сразу несколько
       букв. Уступает вместо неё знак — см. секцию слева. */
    .app-header__section--center {
        position: static;
        transform: none;
        pointer-events: auto;
        flex: 0 0 auto;
        min-width: 0;
    }

    /* `.v-toolbar__content` — flex-строка Vuetify без `gap` и без
       `justify-content`. Пока центр абсолютен, это незаметно: слева секция
       растягивается и отжимает правую к краю. Но здесь центр в потоке и не
       растягивается никто — три секции слипались вплотную (свечение золотой
       кнопки ложилось на знак) и жались влево, оставляя правую половину
       шапки пустой: замер на 613px давал 255px пустоты при нулевых зазорах. */
    .app-header :deep(.v-toolbar__content) {
        gap: 12px;
        justify-content: space-between;
    }

    /* Крайние элементы теперь стоят вплотную к краю `.v-toolbar__content`, а у
       того `overflow: hidden` — кольцо фокуса снаружи срезалось бы по правой
       границе. Рисуем внутрь: #2f285a даёт контраст и на золоте, и на белом. */
    .app-header__page-cta:focus-visible,
    .app-header__profile:focus-visible {
        outline: 2px solid #2f285a;
        outline-offset: -3px;
    }

    /* Ширину уступает знак — он узнаётся и ужатым, а подпись кнопки от сжатия
       теряет смысл. Пол в 72px подобран по замеру: этого хватает, чтобы на
       320px — самом узком экране в обиходе — строка сошлась целиком. */
    .app-header__section--left {
        flex: 0 1 auto;
        min-width: 72px;
    }

    /* Действие страницы забирает мобильную шапку себе: втроём знак, кнопка и
       полный правый кластер не помещаются (на 390px содержимому нужно 497px
       при строке в 344px). Уступают чипы статуса и колокол — кнопка называет
       то, ради чего страницу открыли. Прячем стилем, а не `v-if`: размонтаж
       колокола гасит и WS-подписку, и polling-страховку внутри него.
       Случай не экзотический — verify-otp логинит наставника, не уводя его с
       claim-страницы, то есть через него проходит каждый успешный клейм. */
    .app-header--page-cta .app-header__role-chips,
    .app-header--page-cta .app-header__bell {
        display: none;
    }

    /* Знак — SVG с шириной в пикселях: сама секция сжималась, а он нет и
       вылезал из неё под кнопку (замер на 320px: секция 69px, знак 72px).
       Теперь он вписывается в то, что секции досталось. */
    .app-header__logo {
        min-width: 0;
    }

    .app-header__logo svg {
        max-width: 100%;
        height: auto;
    }

    /* Правый кластер — контролы фиксированного размера: сжиматься ему нечем,
       иначе аватар вылезет за секцию так же, как вылезал знак. */
    .app-header__section--right {
        flex: 0 0 auto;
    }

    .app-header__section {
        gap: 10px;
    }
}

/* Узкие экраны: просветы и кнопку добираем, иначе на 320px строка не сходится
   (знак ужимается сам — см. `max-width` выше). */
@media (max-width: 380px) {
    /* Внешние зазоры ужимаются вместе с внутренними: два по 12px — это та
       ширина, которой здесь как раз не хватает знаку. */
    .app-header :deep(.v-toolbar__content) {
        gap: 8px;
    }

    .app-header__page-cta {
        min-height: 38px;
        padding: 0 10px;
        font-size: var(--t-ui);
    }

    .app-header__section {
        gap: 6px;
    }
}

.app-header__cta {
    height: 40px;
    padding: 0 14px;
    border-radius: 12px;
    border: none;
    background: #dcd8fc;
    color: #705ce2;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    pointer-events: auto;
}

.app-header__cta-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.app-header__cta-icon svg {
    width: 18px;
    height: 18px;
}

.app-header__cta:hover {
    background: #d0cbfb;
}

.app-header__role-chips {
    pointer-events: auto;
}

.app-header__role-badge {
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
}
.app-header__role-badge--mentor {
    background: rgba(123, 101, 247, 0.12);
    color: #7b65f7;
}
.app-header__role-badge--student,
.app-header__role-badge--mentee {
    background: rgba(59, 130, 246, 0.1);
    color: #2563eb;
}

.app-header__profile {
    width: 48px;
    height: 48px;
    padding: 0;
    border-radius: 12px;
    border: 1px solid rgba(148, 163, 184, 0.35);
    background: rgba(255, 255, 255, 0.92);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
    overflow: hidden;
}

.app-header__profile:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.14);
}

.app-header__profile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
}

@media (min-width: 961px) {
    .app-header__burger {
        display: none;
    }
}

@media (max-width: 960px) {
    .app-header-wrapper {
        top: var(--app-floating-gap-mobile);
    }

    .app-header {
        top: var(--app-floating-gap-mobile) !important;
        width: calc(100% - (var(--app-floating-gap-mobile) * 2)) !important;
        padding: 0 10px;
    }

    .app-header__section--left {
        gap: 12px;
    }

    .app-header__section--right {
        gap: 8px;
    }

    .app-header__role-chips--mobile {
        max-width: 112px;
    }

    .app-header__role-chips--mobile :deep(.role-chip) {
        max-width: 112px;
        min-height: 32px;
        padding: 4px 9px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .app-header__role-chips--mobile :deep(.role-chip__suffix) {
        display: none;
    }
}
</style>
